import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { OverviewFormValues } from '../models/overview-form-values';
import { ReceiptNoteType } from '../../../../common/models/enums/receipt-note-type';
import { paloDraftNoteIds, usePaloDraftNotices } from '../../../../common/components/palo/palo-draft-notices';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useInvoiceDetailContext } from '../../context/invoice-detail-context';

/**
 * Tells the positions which supplier and which invoice address the head
 * currently has.
 *
 * The article search used to read the supplier from the saved document. Since
 * head and positions stand on one page, that is wrong twice over: a new
 * invoice has nothing saved, so the search was unfiltered, and a changed one
 * filtered on the supplier it had before — articles of the wrong supplier
 * could reach the document without anyone noticing (Prüfbericht Roland
 * Niedermayr, 28.09.2026). The country of the invoice address goes the same
 * way: goods are booked by it, and a new invoice left the account empty
 * (#2263).
 *
 * The notes of the picked supplier and recipient are asked for here too, so
 * they stand in the head before the first save (#2236).
 *
 * It draws nothing; it only reports what the field holds.
 */
export const PaloInvoiceHeadWatcher = (): null => {
    const { values } = useFormikContext<OverviewFormValues>();
    const { setSupplierAccountId, setSupplierAccountName, setInvoiceCountryCode } = useInvoiceDetailContext();
    const supplierId = values.supplierAccount?.id ?? null;
    const supplierName = values.supplierAccount?.name;
    const invoiceCountryCode = values.invoiceAddress?.countryCode ?? null;

    // The notes follow the head as well; the articles stay those saved.
    const savedItems = useAppSelector(state => state.invoiceDetail.positions.stepData.values.allItems);

    usePaloDraftNotices(
        ReceiptNoteType.Invoices,
        paloDraftNoteIds({
            accountIds: [values.supplierAccount?.id, values.recipientAccount?.id],
            contactIds: [values.supplierContact?.id, values.recipientContact?.id, values.responsibleContact?.id],
            productIds: (savedItems ?? []).map(item => (item as { product?: { id?: number } }).product?.id),
        })
    );

    useEffect(() => {
        setSupplierAccountId?.(supplierId);
        setSupplierAccountName?.(supplierName);

        // Leaves the screen, says nothing: a reader falls back to the saved
        // document, which is right in the wizard where the head is a step of
        // its own.
        return () => {
            setSupplierAccountId?.(undefined);
            setSupplierAccountName?.(undefined);
        };
    }, [setSupplierAccountId, setSupplierAccountName, supplierId, supplierName]);

    useEffect(() => {
        setInvoiceCountryCode?.(invoiceCountryCode);

        return () => setInvoiceCountryCode?.(undefined);
    }, [setInvoiceCountryCode, invoiceCountryCode]);

    return null;
};
